/**
 * Base garments per colour or size (BLU-1522): the pure group logic shared by
 * the 3PL supply composer (`useSupplyComposer`) and the own base garment
 * editor (`useOwnBlankGroups`). Both keep the same S2 shape: a default
 * garment, plus up to five groups of store values, each on another garment.
 * A store value sits on one garment at most.
 */
import type { PreviewProduct } from './types';

/** The most extra base garments one product takes beside its default (the server's limit). */
export const GARMENT_GROUPS_MAX = 5;

export type GarmentSplit = 'colour' | 'size';

/** The key a store value is grouped and looked up by: trimmed, lower case, '' for none (the server's own rule). */
export function garmentValueKey(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/** A product's distinct colours (or sizes) in product order, with their variant counts. */
export function productValues(
  product: Pick<PreviewProduct, 'variants'>,
  dimension: GarmentSplit,
): Array<{ value: string; count: number }> {
  const out = new Map<string, { value: string; count: number }>();
  for (const variant of product.variants) {
    const raw = dimension === 'colour' ? variant.colourName : variant.sizeName;
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (!value) continue;
    const entry = out.get(garmentValueKey(value));
    if (entry) entry.count += 1;
    else out.set(garmentValueKey(value), { value, count: 1 });
  }
  return [...out.values()];
}

export type GarmentMoveResult<G> = { outcome: 'assigned'; groups: G[] } | { outcome: 'too_many' };

/**
 * Put `values` on one garment: every group gives them up (a group left with
 * none is dropped), then the target takes them. A `null` target key is the
 * default garment, which takes them back with no group of its own. Another
 * garment joins its group, or starts one; past `max` groups nothing changes
 * and the answer is `too_many`. `strip` drops whatever else a group keeps per
 * value (the supply composer's matches).
 */
export function moveValuesToGarment<G extends { storeValues: string[] }>(
  groups: readonly G[],
  values: readonly string[],
  target: { key: string | null; create: (storeValues: string[]) => G },
  options: {
    /** A group's garment key, compared with `target.key` as given (callers upper-case both). */
    keyOf: (group: G) => string;
    strip?: (group: G, moving: ReadonlySet<string>) => G;
    max?: number;
  },
): GarmentMoveResult<G> {
  const moving = new Set(values.map(garmentValueKey));
  let next: G[] = groups
    .map((group): G => {
      const kept: G = { ...group, storeValues: group.storeValues.filter((value) => !moving.has(garmentValueKey(value))) };
      return options.strip ? options.strip(kept, moving) : kept;
    })
    .filter((group) => group.storeValues.length > 0);
  if (target.key !== null) {
    const at = next.findIndex((group) => options.keyOf(group) === target.key);
    if (at >= 0) {
      next = next.slice();
      next[at] = { ...next[at]!, storeValues: [...next[at]!.storeValues, ...values] };
    } else if (next.length >= (options.max ?? GARMENT_GROUPS_MAX)) {
      return { outcome: 'too_many' };
    } else {
      next = [...next, target.create([...values])];
    }
  }
  return { outcome: 'assigned', groups: next };
}
